import type { SupportedLocale } from "./locales";

type TranslationDictionary = Record<string, Record<string, string>>;

type TranslationLoader<ModuleName extends string> = (locale: SupportedLocale, moduleName: ModuleName) => Promise<TranslationDictionary>;

const loadedModulesByLocale = new Map<string, Set<string>>();
const inFlightByLocale = new Map<string, Map<string, Promise<TranslationDictionary>>>();

export function markModulesLoaded<ModuleName extends string>(locale: SupportedLocale, modules: ModuleName[]): void {
  const loadedModules = loadedModulesByLocale.get(locale) ?? new Set<string>();
  for (const moduleName of modules) {
    loadedModules.add(moduleName);
  }
  loadedModulesByLocale.set(locale, loadedModules);
}

export function isModuleLoaded<ModuleName extends string>(locale: SupportedLocale, moduleName: ModuleName): boolean {
  return loadedModulesByLocale.get(locale)?.has(moduleName) ?? false;
}

export async function ensureModuleLoadedWithSharedState<ModuleName extends string>(
  locale: SupportedLocale,
  moduleName: ModuleName,
  loadModule: TranslationLoader<ModuleName>,
): Promise<TranslationDictionary | undefined> {
  if (isModuleLoaded(locale, moduleName)) {
    return undefined;
  }

  const inFlightForLocale = inFlightByLocale.get(locale) ?? new Map<string, Promise<TranslationDictionary>>();
  inFlightByLocale.set(locale, inFlightForLocale);

  const existingLoadPromise = inFlightForLocale.get(moduleName);
  if (existingLoadPromise) {
    return await existingLoadPromise;
  }

  const loadPromise = loadModule(locale, moduleName).then((moduleTranslations) => {
    markModulesLoaded(locale, [moduleName]);
    return moduleTranslations;
  });

  inFlightForLocale.set(moduleName, loadPromise);
  try {
    return await loadPromise;
  } finally {
    inFlightForLocale.delete(moduleName);
  }
}
